<template>
  <router-view v-if="isEmbeddedRoute" :key="$route.path" />
  <UiToaster v-else>
    <AppVersionRefreshGate :loaded-version="loadedAppVersion" />
    <router-view :key="$route.path" />
    <Alert />
  </UiToaster>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import Alert from '@/components/common/alert.vue'
import { AppVersionRefreshGate, UiToaster, initHelpScoutBeacon, setHelpScoutBeaconHidden, useApi } from '@shopexperts/shared';
import { shouldHideHelpScoutBeacon } from "@/app/utils/helpScoutBeaconVisibility";
import { loadedAppVersion } from "@/generated/appVersion";
import { useConvexAuthToken } from "@/app/composables/useConvexAuthToken";
import { useAuthStore } from "@/store/auth";
import {
  clearProjectsConvexAuth,
  configureProjectsConvexAuth,
  restartProjectsConvexClientAuth,
} from "@/v2Projects/convexClient";
import { useFeatureFlags } from "@/app/composables/useFeatureFlags";
import { captureConvexError } from "@/v2Projects/telemetry";
import { verifyApiAuthForConvex } from "@/v2Projects/authVerification";

const { isEnabled } = useFeatureFlags();
const authStore = useAuthStore();
const { fetchConvexToken, clearConvexTokenCache } = useConvexAuthToken();
const route = useRoute();
const router = useRouter();
const api = useApi();
const helpscoutBeaconId = '6a409334-7d5c-4b2d-96eb-20616f4960d9';
const isEmbeddedRoute = computed(() => Boolean(route.meta.embedded));
const isHelpScoutHiddenRoute = computed(() => shouldHideHelpScoutBeacon(route.path));

watch(
  isHelpScoutHiddenRoute,
  (isHidden) => {
    if (!isHidden) {
      initHelpScoutBeacon(helpscoutBeaconId);
    }
    setHelpScoutBeaconHidden(isHidden);
  },
  { immediate: true },
);

if (isEnabled("enable_v2_projects")) {
  configureProjectsConvexAuth({
    fetchToken: async ({ forceRefreshToken }: { forceRefreshToken: boolean }) => {
      return await fetchConvexToken(forceRefreshToken);
    },
    clearTokenCache: clearConvexTokenCache,
    verifyApiAuth: () => verifyApiAuthForConvex({
      checkApiAuth: () => api.public.auth.check(),
      captureError: captureConvexError,
    }),
    onLogout: async (reason, error) => {
      clearProjectsConvexAuth();
      clearConvexTokenCache();
      if (error) {
        captureConvexError(error, {
          operation: "convex_auth.logout_fallback",
          level: "error",
          extra: { reason },
        });
      }
      authStore.logout();
      await router.push("/login");
    },
  });

  watch(
    () => authStore.token,
    (nextToken) => {
      if (!nextToken) {
        clearProjectsConvexAuth();
        clearConvexTokenCache();
        return;
      }

      restartProjectsConvexClientAuth();
    },
    { immediate: true, flush: "sync" },
  );
}
</script>
